/**
 * Animate Panel
 * Timeline-based animation controls for layers
 */

import React from 'react';
import {
  Play,
  Pause,
  Square,
  SkipBack,
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  Clock,
  Settings2,
  Repeat,
  Volume2,
  VolumeX,
  Music,
} from 'lucide-react';
import { useAnimationStore, useTemplateStore } from '@/stores';
import { getPresetsByCategory } from '@/lib/animation/presets';
import type { AnimationTrack, AnimationPreset } from '@/types/animation';
import type { AudioLayer } from '@/types/layers';
import { isAudioLayer } from '@/types/layers';

export function AnimatePanel() {
  const {
    timeline,
    playbackState,
    currentTime,
    duration,
    selectedTrackId,
    play,
    pause,
    stop,
    seek,
    addTrackFromPreset,
    updateTrack,
    deleteTrack,
    setTrackEnabled,
    selectTrack,
    initTimeline,
  } = useAnimationStore();

  const { selectedLayerIds, currentTemplate, updateLayer } = useTemplateStore();

  const isPlaying = playbackState === 'playing';
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get audio layers from template
  const audioLayers = React.useMemo(() => {
    if (!currentTemplate) return [];
    return currentTemplate.layers.filter(isAudioLayer);
  }, [currentTemplate]);

  // Get layer name by ID
  const getLayerName = (layerId: string): string => {
    if (!currentTemplate) return layerId;
    const layer = currentTemplate.layers.find(l => l.id === layerId);
    return layer?.name ?? layerId;
  };

  // Add animation to selected layer
  const handleAddPreset = (preset: AnimationPreset) => {
    if (selectedLayerIds.length === 0) return;
    if (!timeline) {
      initTimeline();
    }
    addTrackFromPreset(selectedLayerIds[0], preset);
  };

  // Handle playhead scrub
  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    seek(Number(e.target.value));
  };

  // Presets organized by category
  const presetCategories = getPresetsByCategory();

  return (
    <div className="space-y-4">
      {/* Playback controls */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => seek(0)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
            title="Go to start"
          >
            <SkipBack size={14} />
          </button>
          {isPlaying ? (
            <button
              onClick={pause}
              className="w-9 h-9 flex items-center justify-center bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
              title="Pause"
            >
              <Pause size={16} />
            </button>
          ) : (
            <button
              onClick={() => play(currentTemplate?.layers)}
              className="w-9 h-9 flex items-center justify-center bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
              title="Play"
            >
              <Play size={16} className="ml-0.5" />
            </button>
          )}
          <button
            onClick={stop}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
            title="Stop"
          >
            <Square size={12} />
          </button>
        </div>

        {/* Timeline scrubber */}
        <div className="flex-1 h-6 bg-white/5 rounded relative cursor-pointer group">
          <div
            className="absolute top-0 left-0 h-full bg-primary/20 rounded"
            style={{ width: `${progress}%` }}
          />
          <div
            className="absolute top-0 w-0.5 h-full bg-primary transition-opacity"
            style={{ left: `${progress}%` }}
          />
          <input
            type="range"
            min={0}
            max={duration || 1000}
            value={currentTime}
            onChange={handleScrub}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Time display */}
        <div className="flex items-center gap-1 text-xs text-fg-muted tabular-nums min-w-[80px]">
          <Clock size={12} />
          <span>{(currentTime / 1000).toFixed(2)}s</span>
          <span className="text-fg-subtle">/</span>
          <span>{(duration / 1000).toFixed(2)}s</span>
        </div>
      </div>

      {/* Audio tracks section */}
      {audioLayers.length > 0 && (
        <div className="space-y-1">
          <h3 className="label mb-2 flex items-center gap-2">
            <Music size={14} />
            Audio Tracks
          </h3>
          {audioLayers.map(layer => (
            <AudioTrackRow
              key={layer.id}
              layer={layer}
              onUpdateVolume={(volume) => {
                updateLayer(layer.id, {
                  content: { ...layer.content, volume },
                });
              }}
              onToggleMute={() => {
                updateLayer(layer.id, {
                  visible: !layer.visible,
                });
              }}
              onToggleLoop={() => {
                updateLayer(layer.id, {
                  content: { ...layer.content, loop: !layer.content.loop },
                });
              }}
            />
          ))}
        </div>
      )}

      {/* Animation tracks */}
      {timeline && timeline.tracks.length > 0 && (
        <div className="space-y-1">
          <h3 className="label mb-2">Animation Tracks</h3>
          {timeline.tracks.map(track => (
            <TrackRow
              key={track.id}
              track={track}
              layerName={getLayerName(track.layerId)}
              isSelected={selectedTrackId === track.id}
              onSelect={() => selectTrack(track.id)}
              onToggleEnabled={() => setTrackEnabled(track.id, !track.enabled)}
              onDelete={() => deleteTrack(track.id)}
            />
          ))}
        </div>
      )}

      {/* Track settings - shown when a track is selected */}
      {selectedTrackId && timeline && (
        <TrackSettings
          track={timeline.tracks.find(t => t.id === selectedTrackId)!}
          onUpdate={(updates) => updateTrack(selectedTrackId, updates)}
        />
      )}

      {/* Animation presets */}
      <div className="space-y-3">
        <h3 className="label">Add Animation</h3>
        {selectedLayerIds.length === 0 ? (
          <p className="text-xs text-fg-subtle">
            Select a layer to add animations
          </p>
        ) : (
          <div className="space-y-3">
            {Object.entries(presetCategories).map(([category, presets]) => (
              <PresetCategory
                key={category}
                category={category}
                presets={presets}
                onSelect={handleAddPreset}
              />
            ))}
          </div>
        )}
      </div>

      {/* Empty state */}
      {(!timeline || timeline.tracks.length === 0) && selectedLayerIds.length > 0 && (
        <p className="text-sm text-fg-subtle text-center py-4">
          Click a preset above to add animation effects to the selected layer.
        </p>
      )}
    </div>
  );
}

// Audio track row component
interface AudioTrackRowProps {
  layer: AudioLayer;
  onUpdateVolume: (volume: number) => void;
  onToggleMute: () => void;
  onToggleLoop: () => void;
}

function AudioTrackRow({
  layer,
  onUpdateVolume,
  onToggleMute,
  onToggleLoop,
}: AudioTrackRowProps) {
  const isMuted = !layer.visible;
  const volume = layer.content.volume ?? 1;
  const duration = layer.content.duration ?? 0;

  return (
    <div
      className={`
        group flex items-center gap-2 px-2 py-2 rounded-lg bg-white/5 transition-colors
        ${isMuted ? 'opacity-50' : ''}
      `}
    >
      {/* Mute toggle */}
      <button
        onClick={onToggleMute}
        className="w-6 h-6 flex items-center justify-center hover:bg-white/10 rounded"
        title={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </button>

      {/* Track info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{layer.name}</div>
        <div className="text-xs text-fg-subtle">
          {duration > 0 ? `${duration.toFixed(1)}s` : 'Audio'}
          {layer.content.loop && ' • Loop'}
        </div>
      </div>

      {/* Volume slider */}
      <div className="flex items-center gap-2 w-24">
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={(e) => onUpdateVolume(Number(e.target.value))}
          className="w-full h-1 bg-white/20 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
          title={`Volume: ${Math.round(volume * 100)}%`}
        />
        <span className="text-xs text-fg-muted w-8 text-right">
          {Math.round(volume * 100)}%
        </span>
      </div>

      {/* Loop toggle */}
      <button
        onClick={onToggleLoop}
        className={`
          w-6 h-6 flex items-center justify-center rounded transition-colors
          ${layer.content.loop ? 'bg-primary/20 text-primary' : 'hover:bg-white/10 text-fg-muted'}
        `}
        title={layer.content.loop ? 'Disable loop' : 'Enable loop'}
      >
        <Repeat size={12} />
      </button>
    </div>
  );
}

// Track row component
interface TrackRowProps {
  track: AnimationTrack;
  layerName: string;
  isSelected: boolean;
  onSelect: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
}

function TrackRow({
  track,
  layerName,
  isSelected,
  onSelect,
  onToggleEnabled,
  onDelete,
}: TrackRowProps) {
  return (
    <div
      className={`
        group flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors
        ${isSelected ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}
        ${!track.enabled ? 'opacity-50' : ''}
      `}
      onClick={onSelect}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleEnabled();
        }}
        className="w-6 h-6 flex items-center justify-center hover:bg-white/10 rounded"
      >
        {track.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{track.name}</div>
        <div className="text-xs text-fg-subtle truncate">{layerName}</div>
      </div>

      <div className="text-xs text-fg-muted tabular-nums">
        {(track.startTime / 1000).toFixed(1)}s
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="w-6 h-6 flex items-center justify-center hover:bg-white/10 hover:text-accent-red rounded opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// Track settings component
interface TrackSettingsProps {
  track: AnimationTrack;
  onUpdate: (updates: Partial<AnimationTrack>) => void;
}

function TrackSettings({ track, onUpdate }: TrackSettingsProps) {
  const getTrackDuration = () => {
    if (track.duration !== undefined) return track.duration;
    if (track.keyframes.length === 0) return 0;
    return Math.max(...track.keyframes.map(kf => kf.time));
  };

  return (
    <div className="p-3 bg-white/5 rounded-lg space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-fg">
        <Settings2 size={14} />
        Track Settings
      </div>

      {/* Timing controls */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-fg-muted block mb-1">Delay (ms)</label>
          <input
            type="number"
            min={0}
            step={100}
            value={track.startTime}
            onChange={(e) => onUpdate({ startTime: Math.max(0, Number(e.target.value)) })}
            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-fg"
          />
        </div>
        <div>
          <label className="text-xs text-fg-muted block mb-1">Duration (ms)</label>
          <input
            type="number"
            min={100}
            step={100}
            value={track.duration ?? getTrackDuration()}
            onChange={(e) => onUpdate({ duration: Math.max(100, Number(e.target.value)) })}
            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-fg"
          />
        </div>
      </div>

      {/* Repeat controls */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Repeat size={12} className="text-fg-muted" />
          <label className="text-xs text-fg-muted">Repeat</label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-subtle block mb-1">Count</label>
            <select
              value={track.repeat?.count === 'infinite' ? 'infinite' : (track.repeat?.count ?? 1)}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '1') {
                  onUpdate({ repeat: undefined });
                } else {
                  onUpdate({
                    repeat: {
                      count: value === 'infinite' ? 'infinite' : Number(value),
                      direction: track.repeat?.direction ?? 'normal',
                      delay: track.repeat?.delay,
                    }
                  });
                }
              }}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-fg"
            >
              <option value="1">Once</option>
              <option value="2">2x</option>
              <option value="3">3x</option>
              <option value="5">5x</option>
              <option value="infinite">Infinite</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-fg-subtle block mb-1">Direction</label>
            <select
              value={track.repeat?.direction ?? 'normal'}
              onChange={(e) => {
                if (!track.repeat || track.repeat.count === 1) return;
                onUpdate({
                  repeat: {
                    ...track.repeat,
                    direction: e.target.value as 'normal' | 'reverse' | 'alternate' | 'alternate-reverse',
                  }
                });
              }}
              disabled={!track.repeat || track.repeat.count === 1}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-fg disabled:opacity-50"
            >
              <option value="normal">Normal</option>
              <option value="reverse">Reverse</option>
              <option value="alternate">Alternate</option>
              <option value="alternate-reverse">Alt-Reverse</option>
            </select>
          </div>
        </div>
        {track.repeat && track.repeat.count !== 1 && (
          <div>
            <label className="text-xs text-fg-subtle block mb-1">Repeat Delay (ms)</label>
            <input
              type="number"
              min={0}
              step={100}
              value={track.repeat.delay ?? 0}
              onChange={(e) => onUpdate({
                repeat: {
                  ...track.repeat!,
                  delay: Math.max(0, Number(e.target.value)),
                }
              })}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-fg"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Preset category component
interface PresetCategoryProps {
  category: string;
  presets: AnimationPreset[];
  onSelect: (preset: AnimationPreset) => void;
}

function PresetCategory({ category, presets, onSelect }: PresetCategoryProps) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg w-full"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {category}
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-1.5 mt-2 ml-4">
          {presets.map(preset => (
            <button
              key={preset}
              onClick={() => onSelect(preset)}
              className="px-2.5 py-1 bg-white/5 rounded text-xs font-medium hover:bg-white/10 transition-colors"
            >
              {formatPresetName(preset)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Format preset name for display
function formatPresetName(preset: string): string {
  // fadeInUp -> Fade In Up
  return preset
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}
